import React, { useEffect, useMemo, useState } from "react";
import { loadAppSettingsHybrid, saveAppSettings, ensureAppSettings } from "../../../services/data";
const DEFAULTS = {
  marketplace_enabled: true,
  feature_flags: { marketplace: true, myshop: true, teams: false },
  default_currency: "MAD",
  i18n: { locales: ["fr","ar","en","ru"], default: "fr" },
  branding: { name: "Form2Doc", logoURL: "" },
};

const OWNER_UID = import.meta.env.VITE_OWNER_UID;

export default function SettingsTab(){
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // util pour maj nested
  const upd = (path, val) => {
    setForm(prev => {
      const next = structuredClone(prev);
      const parts = path.split(".");
      let cur = next;
      for (let i=0;i<parts.length-1;i++) cur = cur[parts[i]];
      cur[parts.at(-1)] = val;
      return next;
    });
  };

  useEffect(() => {
    if (!OWNER_UID) {
      setError("VITE_OWNER_UID manquant dans .env.local");
      setLoading(false);
      return;
    }
    let unsub;
    (async () => {
      try {
        // S'assure que app_settings existe (fusion depuis anciens docs si besoin)
        await ensureAppSettings(OWNER_UID); // crée le doc si absent
        // Ecoute hybride: cache local immédiat + live Firestore
        unsub = loadAppSettingsHybrid(OWNER_UID, (live) => {
          const incoming = live || {};
          setForm(prev => ({ ...DEFAULTS, ...prev, ...incoming }));
          setLoading(false);
        });
      } catch (e) {
        console.error(e);
        setError(e?.message || "Erreur chargement settings");
        setLoading(false);
      }
    })();
    return () => { try { unsub && unsub(); } catch {} };
  }, []);

  const onSave = async () => {
    if (!OWNER_UID) return;
    setSaving(true);
    try {
      // merge partiel dans app_settings
      await saveAppSettings(OWNER_UID, form);
      // (option) petit feedback
      alert("Réglages enregistrés.");
    } catch (e) {
      console.error(e);
      alert("Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="muted">Chargement…</div>;
  if (error) return <div className="muted" style={{color:"#c53030"}}>Erreur: {error}</div>;

  return (
    <>
      <div className="page-header">
        <h2 className="page-title">Réglages du site</h2>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>

      <div className="form-grid">
        <section className="form-section">
          <h3 className="section-title">Général</h3>

          <div className="form-group">
            <label className="form-label">Activer le marketplace</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={!!form.marketplace_enabled}
                onChange={(e)=>upd("marketplace_enabled", e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Fonctionnalités</label>
            <div className="inline-switches">
              {[
                ["marketplace", "Marketplace"],
                ["myshop", "MyShop"],
                ["teams", "Teams"],
              ].map(([key, label]) => (
                <div className="switch-item" key={key}>
                  <label className="switch small">
                    <input
                      type="checkbox"
                      checked={!!form.feature_flags?.[key]}
                      onChange={(e)=>upd(`feature_flags.${key}`, e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="switch-label">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Devise par défaut</label>
            <input
              className="form-input"
              type="text"
              value={form.default_currency || ""}
              onChange={(e)=>upd("default_currency", e.target.value.toUpperCase())}
              placeholder="MAD"
            />
          </div>
        </section>

        <section className="form-section">
          <h3 className="section-title">Langues</h3>
          <div className="form-group">
            <label className="form-label">Locales actives</label>
            <input
              className="form-input"
              type="text"
              value={(form.i18n?.locales || []).join(",")}
              onChange={(e)=>upd("i18n.locales", e.target.value.split(",").map(s=>s.trim()).filter(Boolean))}
              placeholder="fr, ar, en, ru"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Langue par défaut</label>
            <input
              className="form-input"
              type="text"
              value={form.i18n?.default || ""}
              onChange={(e)=>upd("i18n.default", e.target.value.trim())}
              placeholder="fr"
            />
          </div>
        </section>

        <section className="form-section">
          <h3 className="section-title">Branding</h3>
          <div className="form-group">
            <label className="form-label">Nom du site</label>
            <input
              className="form-input"
              type="text"
              value={form.branding?.name || ""}
              onChange={(e)=>upd("branding.name", e.target.value)}
              placeholder="Form2Doc"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Logo URL</label>
            <input
              className="form-input"
              type="url"
              value={form.branding?.logoURL || ""}
              onChange={(e)=>upd("branding.logoURL", e.target.value)}
              placeholder="https://…"
            />
          </div>
        </section>
      </div>
    </>
  );
}
